import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Bookmark } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Level } from "types";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { StyledSnackBarFavourite } from "../../components/CardCta";
import { Spinner } from "../../components/Spinner";
import { useFavouritesMutation } from "../../queries";
import { useAuthStore, useLanguageStore } from "../../stores";
import { getLearningObjectType } from "../../utils/general";

type IFavourite = {
  isStandAlone: boolean,
  id: number,
  rootId: number | null | undefined,
  courseId: number | null | undefined,
  learningObjectTypology: Level,
  isFavourite: boolean
}

export const Favourite = ({
  isStandAlone,
  id,
  rootId,
  courseId,
  learningObjectTypology,
  isFavourite
}: IFavourite) => {

  const { t } = useTranslation();
  const theme = useTheme();
  const queryClient = useQueryClient();

  const language = useLanguageStore(state => state.language)?.id;
  const userData = useAuthStore(state => state.userData);
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const learnerId = useAuthStore(state => state.userData?.id_user) ?? "";
  const initiativeId = useAuthStore(state => state.userData?.initiative_id) ?? "";
  const corporateId = useAuthStore(state => state.userData?.organization_id) ?? "";
  const paramsGet =
  // eslint-disable-next-line max-len
  `corporate-id=${corporateId}&domain-id=${initiativeId}&learnerId=${learnerId}&learning-object-id=${id}&learning-object-type=${getLearningObjectType(learningObjectTypology)}`;

  const uriGet = "/social-rating/rating-statistics";
  
  const [ariaMessage, setAriaMessage] = useState("");
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  

  let invalidateKeys: string[] = [];

  const ratingStatistic = queryClient.getQueryData(
    [sessionToken, uriGet, paramsGet, ...invalidateKeys]
  );

  // Determina se ci troviamo nella pagina bookmarks
  const isBookmarksPage = location.pathname.includes("/bookmarks");

  // TO ADD IN PARAMS IN FAVOURITES RETRIEVED FROM LO
  const favParentId = !isStandAlone ? (!courseId && rootId) ? rootId : courseId ?? null : null;

  const favGrandParentId = (!isStandAlone
    && favParentId
    && favParentId !== rootId
  ) ? rootId : null;

  const loStatistic = {
    userFavourite: ratingStatistic
      //@ts-ignore 
      ? Boolean(ratingStatistic?.userRating.isFavouriteForLearner && ratingStatistic?.objectStatistics?.learningObjectId)
      : Boolean(isFavourite)
  };

  // Prepara le query da invalidare solo se siamo nella pagina bookmarks
  const getInvalidateContentQueries = () => {
    if (!isBookmarksPage || !userData) return [];

    // Ritorna un array con un semplice marker per indicare che dobbiamo invalidare le query dei preferiti
    return [{
      apiPath: "", // Non serve un path specifico, useremo pattern matching
      isSeedingSession,
      language,
      additionalKeys: []
    }];
  };
  
  const {
    mutate: addFavouriteMutation,
    isSuccess: isFavouriteAdded,
    isPending: isAddingFavouriteLoading
  } = useFavouritesMutation({
    grandParentId: favGrandParentId,
    learningObjectId: id,
    learningObjectTypology: learningObjectTypology,
    method: "POST",
    parentId: favParentId,
    invalidateContentQueries: getInvalidateContentQueries()
  });

  const {
    mutate: removeFavouriteMutation,
    isSuccess: isFavouriteRemoved,
    isPending: isRemovingFavouriteLoading
  } = useFavouritesMutation({
    invalidateContentQueries: getInvalidateContentQueries(),
    invalidateDelay: isBookmarksPage ? 2000 : 0, // 2 secondi di delay solo nella pagina bookmarks
    learningObjectId: id,
    learningObjectTypology: learningObjectTypology,
    method: "DELETE"
  });

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    // Handle added favourite
    if (isFavouriteAdded && loStatistic.userFavourite) {
      setSnackbarVisible(true);
      setAriaMessage(t("has_been_added"));
      timer = setTimeout(() => {
        setSnackbarVisible(false);
      }, 2800);
    }

    // Handle removed favourite
    else if (isFavouriteRemoved && !loStatistic.userFavourite) {
      setSnackbarVisible(true);
      setAriaMessage(t("has_been_removed"));
      timer = setTimeout(() => {
        setSnackbarVisible(false);
      }, 2800);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [
    isFavouriteAdded,
    isFavouriteRemoved,
    loStatistic.userFavourite
  ]);

  return (
    <Stack>
      <StyledSnackBarFavourite
        $visible={ snackbarVisible }
        id="snackBarFavourite"
        onClick={ ()=> null }
      >
        <Typography
          aria-hidden="true"
          color={ theme.customColors.textPrimary }
          fontSize={ theme.spacing(1.625) }
          fontWeight={ 500 }
        >
          { loStatistic.userFavourite ? t("item_added_favourites") : t("item_removed_favourites") }
        </Typography>
      </StyledSnackBarFavourite>
      <ButtonDigited
        id={ `content-detail-favourite-${id}` }
        data-testid="content-detail-favourite-toggle"
        aria-label={ t(loStatistic.userFavourite ? "remove_from_favourites" : "add_to_favourites") }
        aria-pressed={ loStatistic.userFavourite }
        title={ t(loStatistic.userFavourite ? "remove_from_favourites" : "add_to_favourites") }
        // disabled={ disabled }
        className="bookmark"
        typology="icononly"
        buttonSize="md"
        onMouseDown={ (e) => e.preventDefault() }
        onClick={ (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (loStatistic.userFavourite) {
            removeFavouriteMutation();
            return;
          } 
          addFavouriteMutation();
        } }

      >
        <>
          { /* ICON FAVOURITE */ }
          {
            (isAddingFavouriteLoading || isRemovingFavouriteLoading) ?
              <Spinner />
              : (
                <Bookmark
                  fill={ loStatistic.userFavourite ? theme.customColors.textPrimary : "transparent" }
                  stroke={ theme.customColors.textPrimary }
                  aria-hidden={ !loStatistic.userFavourite ? "true" : undefined }
                />
              )
          }
          <Box
            aria-live="polite"
            sx={ {
              position: "absolute",
              width: 1,
              height: 1,
              overflow: "hidden",
              clip: "rect(0 0 0 0)",
              whiteSpace: "nowrap"
            } }
          >
            { ariaMessage }
          </Box>
        </>
      </ButtonDigited>
    </Stack>

  );
};
